import AutocompleteCities from 'components/ui/AutocompleteCities/AutocompleteCities';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faCalendar, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MoManualRooms from './MoManualRooms';
import { useTranslation } from 'react-i18next';
import useModalClass from 'hooks/useModalClass';
import { Order } from 'ts/shared/interfaces/MOResponse';
import { formatSearchData, getMyOrdersCurrencies, getSearchData, manualBooking } from 'services/myOrders';
import ScreenshotSupport from '../../ScreenshotSupport';
import withErrorBoundary from '../../withErrorBoundary';
import { moStarsOptions } from 'constants/mo';
import { showError, showSuccess } from 'utils/utils';
import { useSearchParams } from 'react-router-dom';
import { MainContext } from 'contexts/MainContext';

interface MoManualBookProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
}

const getCityCodeFromCity = (city: string) => {
  const normalized = (city || '').replace(/[^a-zA-Z]/g, '').toUpperCase();
  return normalized.slice(0, 3);
};

const getZipFromGooglePlace = (place: any) =>
  place?.address_components?.find((item: any) => item.types?.includes('postal_code'))?.long_name || '';

const MANUAL_BOOK_CURRENCY_PRIORITY = ['EUR', 'USD', 'GBP', 'THB'];

const sortManualBookCurrencies = (currencies: Select[]): Select[] => {
  const prioritySet = new Set(MANUAL_BOOK_CURRENCY_PRIORITY);
  const first: Select[] = [];
  for (const code of MANUAL_BOOK_CURRENCY_PRIORITY) {
    const found = currencies.find((c) => String(c.value).toUpperCase() === code);
    if (found) first.push(found);
  }
  const rest = currencies
    .filter((c) => !prioritySet.has(String(c.value).toUpperCase()))
    .sort((a, b) =>
      String(a.value).toUpperCase().localeCompare(String(b.value).toUpperCase(), 'en')
    );
  return [...first, ...rest];
};

const MoManualBook: FC<MoManualBookProps> = ({ openModal, setOpenModal }) => {
  const classList = useModalClass(openModal);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();
  const moFilterRef = useRef<NonNullable<ReturnType<typeof formatSearchData>>>();
  const { user } = useContext(MainContext);
  const { user: sharedUser } = useContext(MainContext);
  const [orderModel, setOrderModel] = useState<Order>({} as Order);
  const [hotelModel, setHotelModel] = useState<any>({} as any);
  const [selectedDestinationPlaceId, setSelectedDestinationPlaceId] = useState('');
  const [supplierOptions, setSupplierOptions] = useState<Select[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<Select[]>([]);
  const [departmentsOptions, setDepartmentsOptions] = useState<Select[]>([]);
  const [branchesOptions, setBranchesOptions] = useState<Select[]>([]);
  const [usersOptions, setUsersOptions] = useState<Select[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const manualOrderBuilderRef = useRef<(() => Order) | null>(null);
  const { t } = useTranslation();

  const normalizePasteJson = (raw: string): string | undefined => {
    const trimmed = raw.trim();
    if (trimmed.length <= 100) return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed.json === 'string') {
        return JSON.stringify(JSON.parse(parsed.json));
      }
      return JSON.stringify(parsed);
    } catch {
      return undefined;
    }
  };

  const getPasteJson = () => normalizePasteJson(orderModel.param_1 || '');

  const validateModels = () => {
    const isOrderValid = !!orderModel?.segments?.[0]?.itemLocationInfo && !!orderModel?.segments?.[0]?.sysTotalNett;
    const isHotelValid = !!hotelModel?.item?.name;
    const paxesValid = orderModel?.paxes?.every((pax) => !!pax?.firstName && !!pax?.lastName);

    return isOrderValid && isHotelValid && paxesValid;
  }

  const onDuplicateOrderChange = (value: string) => {
    setOrderModel((prev) => ({ ...prev, param_1: value }));
    if (normalizePasteJson(value)) {
      setIsSubmitted(false);
    }
  };

  const save = async () => {
    setIsLoading(true);
    const pasteJSON = getPasteJson();
    setIsSubmitted(!pasteJSON);
    const isValid = !!pasteJSON || validateModels();
    if (isValid) {
      const orderToSave = manualOrderBuilderRef.current?.() ?? orderModel;

      const pasteOrder = pasteJSON ? JSON.parse(pasteJSON) : undefined;
      const response = await manualBooking(
        pasteJSON
          ? {
            pasteJSON,
            departmentId: pasteOrder.departmentId,
            branchId: pasteOrder.branchId,
            userCode: pasteOrder.userC,
          }
          : {
            order: orderToSave,
            hotel: { ...hotelModel, city: { code: orderToSave.segments?.[0]?.sysCityCode, desc: orderToSave.segments?.[0]?.sysCityDesc } },
            departmentId: orderToSave.departmentId,
            branchId: orderToSave.branchId,
            userCode: orderToSave.userC,
          }
      );

      if (response?.orderId) {
        showSuccess(t('order_created_successfully'));
        setSearchParams({ orderId: response.orderId });
        close();
      } else {
        showError(t('something_went_wrong'));
      }
    }
    setIsLoading(false);
  };

  const saveAndClose = async () => {
    await save();
    if (getPasteJson() || validateModels()) {
      close();
    }
  };

  const close = () => {
    setOpenModal(false);
  };
  const toDate = (value?: string) => {
    const date = value ? new Date(value) : new Date();
    return Number.isNaN(date.getTime()) ? new Date() : date;
  };

  const updateSegment = (patch: Record<string, any>) => {
    setOrderModel((prev) => ({
      ...prev,
      segments: [{ ...(prev.segments?.[0] || {}), ...patch }],
    }));
  };

  const updateHotel = (patch: {
    item?: Partial<NonNullable<any['item']>>;
    addressInfo?: Partial<NonNullable<any['addressInfo']>> & { fax?: string };
  }) => {
    setHotelModel((prev: any) => ({
      ...prev,
      item: { ...(prev.item as any), ...(patch.item || {}) },
      addressInfo: { ...(prev.addressInfo as any), ...(patch.addressInfo || {}) },
    }));
  };

  useEffect(() => {
    if (!openModal) return;
    setSelectedDestinationPlaceId('');
    (async () => {
      const [searchData, currencies] = await Promise.all([getSearchData(), getMyOrdersCurrencies()]);
      const options = searchData ? formatSearchData(searchData.searchRequest) : undefined;
      const suppliers = (options?.supplierOptions || []).filter((item) => !!item.value);
      setSupplierOptions(suppliers);
      const sortedCurrencies = sortManualBookCurrencies(currencies);
      setCurrencyOptions(sortedCurrencies);
      if (options) {
        moFilterRef.current = options;
        setDepartmentsOptions(options.departmentsOptions);
        let relatedBranches = options.branchesOptions.map((br) => ({
          ...br,
          data: { ...br.data, visible: br.data?.visible ?? true },
        }));
        let relatedUsers = options.usersOptions.map((u) => ({
          ...u,
          data: { ...u.data, visible: u.data?.visible ?? true },
        }));
        const preferredUser = sharedUser || user;
        if (preferredUser) {
          relatedBranches = options.branchesOptions.map((br) => ({
            ...br,
            data: {
              ...br.data,
              visible:
                br.data?.departmentId === preferredUser.departmentId ||
                preferredUser.departmentId === -1 ||
                br.value === '-1',
            },
          }));
          relatedUsers = options.usersOptions.map((u) => ({
            ...u,
            data: {
              ...u.data,
              visible:
                u.data?.branchId === preferredUser.branchId ||
                preferredUser.branchId === -1 ||
                u.value === '-1',
            },
          }));
        }
        setBranchesOptions(relatedBranches);
        setUsersOptions(relatedUsers);
        if (searchData?.searchRequest) {
          const sr = searchData.searchRequest;
          const preferredUserCode = sharedUser?.userCode ?? user?.userCode ?? sr.userCode;
          const preferredDepartmentId = sharedUser?.departmentId ?? user?.departmentId ?? sr.departmentId;
          const preferredBranchId = sharedUser?.branchId ?? user?.branchId ?? sr.branchId;
          setOrderModel((prev) => ({
            ...prev,
            departmentId: prev.departmentId ?? preferredDepartmentId,
            branchId: prev.branchId ?? preferredBranchId,
            userC: prev.userC ?? preferredUserCode,
          }));
        }
      }
      const segment = orderModel.segments?.[0] as any;
      const defaultCheckIn = segment?.checkIn || new Date().toISOString();
      const defaultCheckOutDate = new Date(defaultCheckIn);
      defaultCheckOutDate.setDate(defaultCheckOutDate.getDate() + 3);
      updateSegment({
        sysSuppCode: segment?.sysSuppCode || ((suppliers[0]?.value as string) || ''),
        sysCurrencyCode: segment?.sysCurrencyCode || ((sortedCurrencies[0]?.value as string) || ''),
        checkIn: defaultCheckIn,
        checkOut: segment?.checkOut || defaultCheckOutDate.toISOString(),
      });
    })();
  }, [openModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectDepartment = (departmentId: string) => {
    const base = moFilterRef.current;
    if (!base) return;
    const relatedBranches = base.branchesOptions.map((br) => ({
      ...br,
      data: {
        ...br.data,
        visible:
          br.data?.departmentId === +departmentId ||
          br.data?.departmentId === -1 ||
          departmentId === '-1',
      },
    }));
    const relatedUsers = base.usersOptions.map((u) => ({
      ...u,
      data: {
        ...u.data,
        visible:
          u.data?.departmentId === +departmentId ||
          u.data?.departmentId === -1 ||
          departmentId === '-1',
      },
    }));
    setBranchesOptions(relatedBranches);
    setUsersOptions(relatedUsers);
    setOrderModel((prev) => ({
      ...prev,
      departmentId: +departmentId,
      branchId: -1,
      userC: -1,
    }));
  };

  const onSelectBranch = (branchId: string) => {
    const base = moFilterRef.current;
    if (!base) return;
    const relatedUsers = base.usersOptions.map((u) => ({
      ...u,
      data: {
        ...u.data,
        visible: u.data?.branchId === +branchId || u.data?.branchId === -1 || branchId === '-1',
      },
    }));
    setUsersOptions(relatedUsers);
    setOrderModel((prev) => ({
      ...prev,
      branchId: +branchId,
      userC: -1,
    }));
  };

  const segment = orderModel.segments?.[0] as any;
  const nights = String(
    Math.max(
      0,
      Math.ceil(
        (toDate(segment?.checkOut).setHours(0, 0, 0, 0) -
          toDate(segment?.checkIn).setHours(0, 0, 0, 0)) /
        86400000
      )
    )
  );

  return createPortal(
    <div
      className="modal-overlay modal-overlay-top"
    >
      <div className={classList} style={{ width: '95%', maxWidth: 1500 }}>
        <div className="modal-title title mb-20">
          <span>Manual Booking</span>

          <div>
            <ScreenshotSupport />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>
        <div className="modal-content modal-body-overflow" style={{ paddingTop: 10 }}>
          <div className="row">
            <div className="col-12 col-md-6" style={{ width: '50%' }}>
              <div className="mo-block">
                <div className="mo-block-title">
                  <span>Destination & Hotel</span>
                </div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-12 mb-15">
                      <div className="position-relative">
                        <AutocompleteCities
                          value={selectedDestinationPlaceId}
                          defaultCity={segment?.itemLocationInfo}
                          isSubmitted={isSubmitted}
                          onSelect={(dest) => {
                            const placeTypes = JSON.parse(dest.types || '[]');
                            const formattedPhone =
                              dest.fullValue?.international_phone_number ||
                              dest.fullValue?.formatted_phone_number ||
                              '';
                            const destValue = (
                              dest.fullValue?.formatted_address ||
                              dest.city ||
                              dest.locationName ||
                              ''
                            ).trim();
                            const cityValue = dest.city || destValue;

                            setSelectedDestinationPlaceId(dest.placeId);
                            updateSegment({
                              itemLocationInfo: destValue,
                              sysCityDesc: cityValue,
                              sysCityCode: getCityCodeFromCity(cityValue),
                            });
                            if (Array.isArray(placeTypes) && placeTypes.includes('establishment')) {
                              updateHotel({
                                item: { name: dest.locationName || hotelModel.item?.name || '' },
                                addressInfo: {
                                  address: dest.fullValue?.formatted_address || hotelModel.addressInfo?.address || '',
                                  phone: formattedPhone || hotelModel.addressInfo?.phone || '',
                                  zip: getZipFromGooglePlace(dest.fullValue) || hotelModel.addressInfo?.zip || '',
                                },
                              });
                            }
                          }}
                          movablePlaceholder="Destination"
                        />
                      </div>
                    </div>

                    <FormGroup
                      onChange={(value) => {
                        const nextCity = String(value || '');
                        updateSegment({
                          sysCityDesc: nextCity,
                          sysCityCode: getCityCodeFromCity(nextCity),
                        });
                      }}
                      value={segment?.sysCityDesc || ''}
                      label="City"
                      className="col-md-8 mb-15 col-12"
                      movablePlaceholder="City"
                      movablePlaceholderSm={true}
                    />
                    <FormGroup
                      onChange={() => { }}
                      value={segment?.sysCityCode || ''}
                      label="City Code"
                      disabled={true}
                      className="col-md-4 mb-15 col-12"
                      movablePlaceholder="City Code"
                      movablePlaceholderSm={true}
                    />
                    <FormGroup
                      onChange={(name) => updateHotel({ item: { name } })}
                      value={hotelModel.item?.name || ''}
                      isSubmitted={isSubmitted}
                      validation={{ required: true }}
                      label="Hotel Name"
                      className="col-md-8 mb-15 col-12"
                      movablePlaceholder="Hotel Name"
                      movablePlaceholderSm={true}
                    />
                    <div className="col-md-4 mb-15 col-12">
                      <SelectCustom
                        options={moStarsOptions}
                        onChange={(star) =>
                          updateHotel({
                            item: {
                              star: Number.parseFloat(String(star || '0')) || 0,
                            },
                          })
                        }
                        value={String(hotelModel.item?.star ?? '')}
                        movablePlaceholder="Stars"
                        movablePlaceholderSm={true}
                      />
                    </div>
                    <div className="col-12 mb-15">
                      <div className="mb-5 text-light">Address</div>
                      <textarea
                        className={`form-control`}
                        value={hotelModel.addressInfo?.address || ''}
                        onChange={(e) => updateHotel({ addressInfo: { address: e.target.value } })}
                        style={{ height: '80px', resize: 'none' }}
                      />
                    </div>
                    <FormGroup
                      onChange={(phone) => updateHotel({ addressInfo: { phone } })}
                      value={hotelModel.addressInfo?.phone || ''}
                      label="Phone"
                      className="col-md-4 col-12 mb-15"
                      movablePlaceholder="Phone"
                      movablePlaceholderSm={true}
                    />
                    <FormGroup
                      onChange={(fax) => updateHotel({ addressInfo: { fax } })}
                      value={(hotelModel.addressInfo as { fax?: string } | undefined)?.fax || ''}
                      label="Fax"
                      className="col-md-4 col-12 mb-15"
                      movablePlaceholder="Fax"
                      movablePlaceholderSm={true}
                    />
                    <FormGroup
                      onChange={(zip) => updateHotel({ addressInfo: { zip } })}
                      value={hotelModel.addressInfo?.zip || ''}
                      label="Zip"
                      className="col-md-4 col-12 mb-15"
                      movablePlaceholder="Zip"
                      movablePlaceholderSm={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="mo-block">
                <div className="mo-block-title">Dates</div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className='col-12 col-md-6 mb-15'>
                      <FormGroupDatepicker
                        className=""
                        onChange={(checkIn) => updateSegment({ checkIn: checkIn.toISOString() })}
                        label="Check In"
                        value={toDate(segment?.checkIn)}
                        movablePlaceholder="CheckIn"
                        movablePlaceholderIcon={faCalendar}
                        maxDate={toDate(segment?.checkOut)}
                      />
                    </div>

                    <div className='col-12 col-md-6 mb-15'>
                      <FormGroupDatepicker
                        onChange={(checkOut) => updateSegment({ checkOut: checkOut.toISOString() })}
                        label="Check Out"
                        value={toDate(segment?.checkOut)}
                        movablePlaceholder="CheckOut"
                        movablePlaceholderIcon={faCalendar}
                        minDate={toDate(segment?.checkIn)}
                      />
                    </div>

                    <div className='col-12 col-md-6 mb-15'>
                      <FormGroup
                        onChange={() => { }}
                        value={nights}
                        label="Nights"
                        disabled={true}
                        movablePlaceholder="Nights"
                      />
                    </div>
                    <div className='col-12 col-md-6 mb-15'>
                      <FormGroupDatepicker
                        onChange={(roomsFirstCXL) =>
                          updateSegment({ roomsFirstCXL: roomsFirstCXL.toISOString() })
                        }
                        label="CXL Date"
                        value={toDate(segment?.roomsFirstCXL)}
                        movablePlaceholder="CXL Date"
                        movablePlaceholderIcon={faCalendar}
                      />

                    </div>
                  </div>
                </div>
              </div>
              <div className="mo-block mt-15">
                <div className="mo-block-title">Supplier</div>
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-12 col-md-6 mb-15">
                      <SelectCustom
                        options={supplierOptions}
                        onChange={(sysSuppCode) => updateSegment({ sysSuppCode: String(sysSuppCode || '') })}
                        value={segment?.sysSuppCode || ''}
                        movablePlaceholder="Supplier"
                      />
                    </div>
                    <div className='col-md-6'></div>
                    <FormGroup
                      onChange={(suppPnr) => updateSegment({ suppPnr })}
                      value={segment?.suppPnr || ''}
                      label="Supp Pnr"
                      className="col-12 col-md-6 mb-15"
                      movablePlaceholder="Supp Pnr"
                      movablePlaceholderSm={true}
                    />
                    <FormGroup
                      onChange={(suppPnrSeg) => updateSegment({ suppPnrSeg })}
                      value={segment?.suppPnrSeg || ''}
                      label="SuppPnrSeg"
                      className="col-12 col-md-6 mb-15"
                      movablePlaceholder="SuppPnrSeg"
                      movablePlaceholderSm={true}
                    />
                    <FormGroup
                      onChange={(totalNett) =>
                        updateSegment({ sysTotalNett: Number(totalNett || 0) })
                      }
                      value={String(segment?.sysTotalNett ?? '')}
                      allowOnlyNumbers
                      label="TotalNett"
                      className="col-12 col-md-6 mb-15"
                      movablePlaceholder="TotalNett"
                      movablePlaceholderSm={true}
                      isSubmitted={isSubmitted}
                      validation={{ required: true }}
                    />
                    <div className="col-12 col-md-6 mb-15 select-custom__sm_padding">
                      <SelectCustom
                        options={currencyOptions}
                        onChange={(sysCurrencyCode) =>
                          updateSegment({ sysCurrencyCode: String(sysCurrencyCode || '') })
                        }
                        value={segment?.sysCurrencyCode || ''}
                        movablePlaceholder="Currency"
                        movablePlaceholderSm={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <MoManualRooms
            orderModel={orderModel}
            setOrderModel={setOrderModel}
            isSubmitted={isSubmitted}
            orderBuilderRef={manualOrderBuilderRef}
          />
          <div className="mo-block mt-15">
            <div className="mo-block-title">User</div>
            <div className="mo-block-content">
              <div className="row">
                <div className="col-12 col-md-4 mb-15 select-custom__menu-long">
                  <SelectCustom
                    options={departmentsOptions}
                    value={String(orderModel.departmentId ?? '-1')}
                    onChange={onSelectDepartment}
                    movablePlaceholder="Department"
                    showHtmlPrev={true}
                  />
                </div>
                <div className="col-12 col-md-4 mb-15 select-custom__menu-long">
                  <SelectCustom
                    options={branchesOptions.filter((br) => br.data?.visible !== false)}
                    value={String(orderModel.branchId ?? '-1')}
                    onChange={onSelectBranch}
                    movablePlaceholder="Branch"
                    showHtmlPrev={true}
                  />
                </div>
                <div className="col-12 col-md-4 mb-15 select-custom__menu-long">
                  <SelectCustom
                    options={usersOptions.filter((u) => u.data?.visible !== false)}
                    value={String(orderModel.userC ?? '-1')}
                    onChange={(userCode) =>
                      setOrderModel((prev) => ({ ...prev, userC: +String(userCode || '-1') }))
                    }
                    movablePlaceholder="User"
                    showHtmlPrev={true}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mo-block mt-15 mb-30">
            <div className="mo-block-title">Duplicate Order</div>
            <div className="mo-block-content">
              <textarea
                className="form-control"
                placeholder="JSON..."
                value={orderModel.param_1 || ''}
                onChange={(e) => onDuplicateOrderChange(e.target.value)}
                style={{ minHeight: 120, resize: 'vertical' }}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <Button
            className="btn btn-primary mr-10 ml-0"
            title={t('save')}
            onClick={save}
            disabled={isLoading}
          />
          <Button
            className="btn btn-primary mr-a ml-0"
            title={'Save & Close'}
            onClick={saveAndClose}
            disabled={isLoading}
          />
          <Button className="btn btn-default" title={t('close')} onClick={close} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default withErrorBoundary(MoManualBook, 'MoManualBook');
